import { QueryCache, QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { createRouter, RouterProvider } from '@tanstack/react-router'
import { StrictMode } from 'react'
import ReactDOM from 'react-dom/client'
import { ApiClientError } from './lib/api'
import { ThemeProvider } from './lib/theme'
import { routeTree } from './routeTree.gen'
import './styles.css'

const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: (error) => {
      // A session that expires mid-visit shouldn't strand the user on a broken
      // page. Send them to /login; the guard preserves where they were.
      if (error instanceof ApiClientError && error.isUnauthorized) {
        const path = window.location.pathname
        if (path !== '/login') {
          window.location.href = `/login?redirect=${encodeURIComponent(path + window.location.search)}`
        }
      }
    },
  }),
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      refetchOnWindowFocus: false,
      retry: (count, error) =>
        !(error instanceof ApiClientError && error.status >= 400 && error.status < 500) &&
        count < 2,
    },
  },
})

const router = createRouter({
  routeTree,
  context: { queryClient },
  defaultPreload: 'intent',
  // Query owns caching; the router shouldn't keep a second, staler copy.
  defaultPreloadStaleTime: 0,
})

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router
  }
}

const rootElement = document.getElementById('root')
if (!rootElement) throw new Error('Root element #root not found')

ReactDOM.createRoot(rootElement).render(
  <StrictMode>
    <ThemeProvider>
      <QueryClientProvider client={queryClient}>
        <RouterProvider router={router} />
      </QueryClientProvider>
    </ThemeProvider>
  </StrictMode>,
)
